import { useState, useCallback } from 'react'
import { format } from 'date-fns'
import { X, UtensilsCrossed, Users, Phone, LayoutGrid, Map } from 'lucide-react'
import { motion } from 'framer-motion'
import { Button } from '@/components/ui/Button'
import { useBodyScrollLock } from '@/components/ui/Modal'
import { Input } from '@/components/ui/Input'
import { useBookingActions } from '@/lib/hooks/useBookingActions'
import { useAreas, useCreateBookingTable, useOccupiedTableIds } from '@/lib/booking/tableAdapter'
import { eventBus } from '@/lib/eventBus/events'
import { slideInRight } from '@/lib/motion'
import type { Area } from '@/lib/types/area'

// ── Mini map (copied from BookingForm) ────────────────────────────────────────
const TABLE_W = 88
const TABLE_H = 88
const MAP_PAD = 16

function MiniMapArea({ area, occupiedIds, selectedIds, shakingId, onTableClick }: {
  area: Area; occupiedIds: Set<string>; selectedIds: Set<string>
  shakingId: string | null; onTableClick: (id: string) => void
}) {
  const tables = area.tables?.filter((t) => !t.deleted && t.metadata) ?? []
  if (tables.length === 0) return null
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity
  for (const t of tables) {
    if (!t.metadata) continue
    minX = Math.min(minX, t.metadata.posX); minY = Math.min(minY, t.metadata.posY)
    maxX = Math.max(maxX, t.metadata.posX + TABLE_W); maxY = Math.max(maxY, t.metadata.posY + TABLE_H)
  }
  const vbX = minX - MAP_PAD, vbY = minY - MAP_PAD
  const vbW = (maxX - minX) + MAP_PAD * 2, vbH = (maxY - minY) + MAP_PAD * 2
  return (
    <div className="mb-3">
      <p className="text-xs font-medium text-[var(--color-text-muted)] uppercase tracking-wide mb-1.5">{area.name}</p>
      <svg viewBox={`${vbX} ${vbY} ${vbW} ${vbH}`} style={{ width: '100%', height: 'auto', display: 'block' }}
        className="border border-[var(--color-border-subtle)] rounded-[var(--radius-md)] bg-[var(--color-bg-base)]">
        {tables.map((table) => {
          if (!table.metadata) return null
          const { posX, posY, rotation = 0 } = table.metadata
          const cx = posX + TABLE_W / 2, cy = posY + TABLE_H / 2
          const occupied = occupiedIds.has(table.documentId)
          const selected = selectedIds.has(table.documentId)
          const shaking = shakingId === table.documentId
          const fill   = occupied ? 'var(--color-bg-elevated)' : selected ? 'var(--color-accent-amber-dim)' : 'var(--color-bg-surface)'
          const stroke = occupied ? 'var(--color-border-default)' : selected ? 'var(--color-accent-amber)' : 'var(--color-border-subtle)'
          const textFill = occupied ? 'var(--color-text-muted)' : selected ? 'var(--color-accent-amber)' : 'var(--color-text-secondary)'
          return (
            <g key={table.documentId} onClick={() => onTableClick(table.documentId)}
              style={{ cursor: occupied ? 'not-allowed' : 'pointer', opacity: occupied ? 0.45 : 1 }}>
              <motion.g animate={shaking ? { x: [0, -6, 6, -6, 6, 0] } : { x: 0 }}
                transition={shaking ? { duration: 0.4 } : {}} transform={`rotate(${rotation}, ${cx}, ${cy})`}>
                <rect x={posX} y={posY} width={TABLE_W} height={TABLE_H} rx={6} style={{ fill, stroke, strokeWidth: 2 }} />
                <text x={cx} y={cy - TABLE_H * 0.08} textAnchor="middle" dominantBaseline="middle"
                  fontSize={TABLE_W * 0.22} fontWeight={600} style={{ fill: textFill }}>{table.name}</text>
                <text x={cx} y={cy + TABLE_H * 0.2} textAnchor="middle" dominantBaseline="middle"
                  fontSize={TABLE_W * 0.16} opacity={0.7} style={{ fill: textFill }}>{table.seatsMax}p</text>
              </motion.g>
            </g>
          )
        })}
      </svg>
    </div>
  )
}

interface WalkInModalProps {
  open: boolean
  onClose: () => void
  defaultTableIds?: string[]
}

export function WalkInModal({ open, onClose, defaultTableIds }: WalkInModalProps) {
  useBodyScrollLock(open)
  const bookingActions = useBookingActions()
  const createBookingTable = useCreateBookingTable()

  const { data: areasResp } = useAreas()
  const areas = areasResp?.data ?? []

  const now = new Date()
  const todayStr = format(now, 'yyyy-MM-dd')
  const timeStr = format(now, 'HH:mm')

  const [people, setPeople] = useState(2)
  const [guestName, setGuestName] = useState('')
  const [guestPhone, setGuestPhone] = useState('')
  const [selectedTableIds, setSelectedTableIds] = useState<Set<string>>(() => new Set(defaultTableIds ?? []))
  const [tableView, setTableView] = useState<'list' | 'map'>('list')
  const [shakingId, setShakingId] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  const occupiedIds = useOccupiedTableIds(todayStr, timeStr, 120)

  const shakeTable = useCallback((id: string) => {
    setShakingId(id)
    setTimeout(() => setShakingId(null), 500)
  }, [])

  const toggleTable = useCallback((tableDocId: string) => {
    if (occupiedIds.has(tableDocId)) { shakeTable(tableDocId); return }
    setSelectedTableIds((prev) => {
      const next = new Set(prev)
      next.has(tableDocId) ? next.delete(tableDocId) : next.add(tableDocId)
      return next
    })
  }, [occupiedIds, shakeTable])

  function findTableWithArea(tableDocId: string) {
    for (const area of areas) {
      const table = area.tables?.find((t) => t.documentId === tableDocId)
      if (table) return { table, area }
    }
    return { table: null, area: null }
  }

  const handleSubmit = async () => {
    setSubmitting(true)
    const tableIdsSnapshot = new Set(selectedTableIds)
    const areasSnapshot = areas.map((a) => ({ ...a, tables: a.tables ?? [] }))

    // Build optimistic bookingTables so the booking appears in the timeline immediately
    const optimisticBookingTables = Array.from(tableIdsSnapshot).flatMap((tableDocId) => {
      for (const area of areasSnapshot) {
        const table = area.tables.find((t) => t.documentId === tableDocId)
        if (table) {
          return [{
            id: -table.id,
            name: table.name,
            seatsMin: table.seatsMin,
            seatsMax: table.seatsMax,
            table,
            areaSnapshot: { bookosId: area.bookosId ?? '', name: area.name, internalNote: area.internalNote ?? '', bookingPriority: area.bookingPriority ?? 0 },
          }]
        }
      }
      return []
    })

    try {
      const tempId = await bookingActions.create({
        bookosId: `walkin-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        date: todayStr,
        time: timeStr,
        duration: 120,
        people,
        status: 'arrived',
        source: 'walkin',
        guest: { name: guestName || 'Walk-in', phone: guestPhone || null, email: null, notificationSms: false, notificationEmail: false },
        bookingTables: optimisticBookingTables,
      } as any)

      // Attach tables on the server once the booking syncs and we have the real documentId
      if (tableIdsSnapshot.size > 0) {
        const handler = ({ tempId: t, serverDocumentId }: { tempId: string; serverDocumentId: string }) => {
          if (t !== tempId) return
          eventBus.off('booking:synced', handler)
          for (const tableDocId of tableIdsSnapshot) {
            for (const area of areasSnapshot) {
              const table = area.tables.find((tbl) => tbl.documentId === tableDocId)
              if (table) { createBookingTable.mutate({ bookingDocumentId: serverDocumentId, table, area }); break }
            }
          }
        }
        eventBus.on('booking:synced', handler)
      }

      onClose()
    } finally {
      setSubmitting(false)
    }
  }

  if (!open) return null

  return (
    <div
      className="fixed inset-0 z-[900] flex justify-end bg-black/50 pt-[env(safe-area-inset-top)] pb-[env(safe-area-inset-bottom)] sm:pt-0 sm:pb-0"
      onClick={() => { if (!submitting) onClose() }}
    >
      <motion.div
        {...slideInRight}
        className="relative flex h-auto min-h-0 w-full max-w-none flex-col overflow-hidden border-l border-[var(--color-border-subtle)] bg-[var(--color-bg-surface)] shadow-2xl sm:h-full sm:max-w-sm"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex shrink-0 items-center justify-between gap-3 border-b border-[var(--color-border-subtle)] p-4">
          <div>
            <div className="flex items-center gap-2">
              <span className="text-[10px] font-semibold uppercase tracking-widest px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-400 border border-amber-500/25">
                Walk-in
              </span>
            </div>
            <h2 className="text-base font-semibold text-[var(--color-text-primary)] mt-1">New Walk-in</h2>
            <p className="text-xs text-[var(--color-text-muted)] mt-0.5">
              {format(now, 'EEE d MMM')} · {timeStr} · 2h
            </p>
          </div>
          <button
            onClick={() => { if (!submitting) onClose() }}
            aria-label="Close walk-in form"
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-[var(--radius-md)] text-[var(--color-text-muted)] transition-colors hover:bg-[var(--color-bg-elevated)] hover:text-[var(--color-text-primary)]"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="min-h-0 flex-1 space-y-5 touch-pan-y overflow-y-auto overscroll-contain p-4 pb-[calc(1rem+env(safe-area-inset-bottom))]">
          {/* Party size */}
          <div>
            <label className="block text-xs font-medium text-[var(--color-text-muted)] uppercase tracking-wider mb-2">
              <Users className="inline h-3 w-3 mr-1" />Guests
            </label>
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => setPeople((p) => Math.max(1, p - 1))}
                className="w-9 h-9 rounded-full border border-[var(--color-border-subtle)] text-[var(--color-text-primary)] hover:bg-[var(--color-bg-elevated)] transition-colors text-lg font-light"
              >
                −
              </button>
              <span className="text-2xl font-semibold text-[var(--color-text-primary)] w-8 text-center tabular-nums">
                {people}
              </span>
              <button
                type="button"
                onClick={() => setPeople((p) => Math.min(100, p + 1))}
                className="w-9 h-9 rounded-full border border-[var(--color-border-subtle)] text-[var(--color-text-primary)] hover:bg-[var(--color-bg-elevated)] transition-colors text-lg font-light"
              >
                +
              </button>
            </div>
          </div>

          {/* Optional guest info */}
          <div className="space-y-2">
            <label className="block text-xs font-medium text-[var(--color-text-muted)] uppercase tracking-wider">
              Guest <span className="normal-case font-normal">(optional)</span>
            </label>
            <Input
              placeholder="Name"
              value={guestName}
              onChange={(e) => setGuestName(e.target.value)}
            />
            <div className="relative">
              <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-[var(--color-text-muted)]" />
              <Input
                placeholder="Phone"
                value={guestPhone}
                onChange={(e) => setGuestPhone(e.target.value)}
                className="pl-8"
              />
            </div>
          </div>

          {/* Table assignment */}
          {areas.length > 0 && (() => {
            const allTables = areas.flatMap((a) => (a.tables ?? []).filter((t) => !t.deleted))
            const hasPositionData = allTables.some((t) => t.metadata?.posX !== undefined)
            return (
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-xs font-medium text-[var(--color-text-muted)] uppercase tracking-wider">
                    <UtensilsCrossed className="inline h-3 w-3 mr-1" />
                    Assign Table
                    {selectedTableIds.size > 0 && (
                      <span className="ml-2 text-[var(--color-accent-amber)] normal-case font-normal">
                        {selectedTableIds.size} selected
                      </span>
                    )}
                  </label>
                  {hasPositionData && (
                    <div className="flex items-center gap-0.5 bg-[var(--color-bg-elevated)] rounded-[var(--radius-sm)] p-0.5">
                      <button type="button" onClick={() => setTableView('list')}
                        className={`flex items-center gap-1 px-2 py-1 rounded text-xs transition-colors ${
                          tableView === 'list'
                            ? 'bg-[var(--color-bg-surface)] text-[var(--color-text-primary)] shadow-sm'
                            : 'text-[var(--color-text-muted)] hover:text-[var(--color-text-secondary)]'
                        }`}>
                        <LayoutGrid className="h-3 w-3" />List
                      </button>
                      <button type="button" onClick={() => setTableView('map')}
                        className={`flex items-center gap-1 px-2 py-1 rounded text-xs transition-colors ${
                          tableView === 'map'
                            ? 'bg-[var(--color-bg-surface)] text-[var(--color-text-primary)] shadow-sm'
                            : 'text-[var(--color-text-muted)] hover:text-[var(--color-text-secondary)]'
                        }`}>
                        <Map className="h-3 w-3" />Map
                      </button>
                    </div>
                  )}
                </div>

                {tableView === 'list' || !hasPositionData ? (
                  <div className="space-y-3">
                    {areas.map((area) => {
                      const tables = area.tables?.filter((t) => !t.deleted) ?? []
                      if (tables.length === 0) return null
                      return (
                        <div key={area.documentId}>
                          <p className="text-[10px] font-medium text-[var(--color-text-muted)] uppercase tracking-wide mb-1.5">
                            {area.name}
                          </p>
                          <div className="flex flex-wrap gap-2">
                            {tables.map((table) => {
                              const selected = selectedTableIds.has(table.documentId)
                              const occupied = occupiedIds.has(table.documentId)
                              const shaking = shakingId === table.documentId
                              return (
                                <motion.button
                                  key={table.documentId}
                                  type="button"
                                  onClick={() => toggleTable(table.documentId)}
                                  animate={shaking ? { x: [0, -4, 4, -4, 4, 0] } : { x: 0 }}
                                  transition={shaking ? { duration: 0.4 } : {}}
                                  className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-[var(--radius-md)] border text-xs font-medium transition-colors ${
                                    occupied
                                      ? 'bg-[var(--color-bg-elevated)] border-[var(--color-border-default)] text-[var(--color-text-muted)] opacity-50 cursor-not-allowed'
                                      : selected
                                      ? 'bg-[var(--color-accent-amber-dim)] border-[var(--color-accent-amber)] text-[var(--color-accent-amber)]'
                                      : 'bg-[var(--color-bg-elevated)] border-[var(--color-border-default)] text-[var(--color-text-secondary)] hover:border-[var(--color-accent-amber)]/50'
                                  }`}
                                >
                                  <UtensilsCrossed className="h-3 w-3" />
                                  {table.name}
                                  <span className="text-[0.65rem] opacity-70">{table.seatsMax}p</span>
                                  {occupied && <span className="text-[0.6rem] opacity-60">✕</span>}
                                </motion.button>
                              )
                            })}
                          </div>
                        </div>
                      )
                    })}
                  </div>
                ) : (
                  <div className="overflow-x-auto pb-2">
                    {areas.map((area) => (
                      <MiniMapArea
                        key={area.documentId}
                        area={area}
                        occupiedIds={occupiedIds}
                        selectedIds={selectedTableIds}
                        shakingId={shakingId}
                        onTableClick={toggleTable}
                      />
                    ))}
                  </div>
                )}
              </div>
            )
          })()}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-[var(--color-border-subtle)] shrink-0">
          <Button
            variant="primary"
            className="w-full"
            onClick={handleSubmit}
            loading={submitting}
            disabled={submitting}
          >
            Create Walk-in
          </Button>
        </div>
      </motion.div>
    </div>
  )
}
